'use client';

import NumberFlow from '@number-flow/react';
import Decimal from 'decimal.js';
import { ComponentProps, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { NumericString } from '@/lib/types/common';
import { Button } from '@/lib/ui-kit/button';
import { Icon } from '@/lib/ui-kit/icons';
import { Input } from '@/lib/ui-kit/input';
import { cn } from '@/lib/ui-kit/utils';
import { capNumber } from '@/lib/utils/capNumber';
import { formatMoney } from '@/lib/utils/formatAmount';
import { isNumberWithinBounds } from '@/lib/utils/isNumberWithinBounds';

function mapRawPosToFormatted(raw: string, formatted: string, oldCaret: number): number {
  // Count how many “valid chars” (digits or '.') were before oldCaret in raw
  let countValidBefore = 0;
  for (let i = 0; i < oldCaret; i++) {
    const ch = raw[i];
    if (/[0-9.]/.test(ch)) {
      countValidBefore++;
    }
  }

  // Now scan the formatted string until we've seen that many valid chars:
  let seen = 0;
  let pos = 0;
  while (pos < formatted.length && seen < countValidBefore) {
    const ch = formatted[pos];
    if (/[0-9.]/.test(ch)) {
      seen++;
    }
    pos++;
  }

  // If we exit because seen == countValidBefore, "pos" is already one past the last valid char.
  // We want the caret to sit right after that, so pos is correct. If formatted is shorter, clamp to length.
  return Math.min(pos, formatted.length);
}

// strip everything except digits & dot
const strip = (raw: string) => raw.replace(/[^0-9.]/g, '');

// Live typing only: keeps a half-typed "1,234." or "0." intact, which the fixed
// `formatMoney` cannot. Settled values go through `formatMoney`.
export function formatNumberString(cleaned: string, allowFractional: boolean): string {
  if (cleaned === '' || cleaned === '.') {
    if (allowFractional) {
      return cleaned === '.' ? '0.' : '';
    }
    return '0';
  }
  const [intPart, decPart = ''] = cleaned.split('.');
  const formattedInt = new Intl.NumberFormat('en-US').format(Number(intPart) || 0);
  if (!allowFractional) {
    return formattedInt;
  }
  if (cleaned.includes('.')) {
    return decPart ? `${formattedInt}.${decPart}` : `${formattedInt}.`;
  }
  return formattedInt;
}

type NumberInputProps = Omit<ComponentProps<'input'>, 'value' | 'onChange'> & {
  value?: number;
  onChange?: (value: number) => void;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  id?: string;
  prefix?: string;
  suffix?: string;
  showButtons?: boolean;
  /** max fraction digits allowed */
  fractional?: number;
  /** Enable NumberFlow animation (default: true) */
  animated?: boolean;
};

export function NumberInput({
  id: idProp,
  value,
  min = 0,
  max = Infinity,
  step = 1,
  disabled = false,
  showButtons = false,
  placeholder = '',
  prefix = '',
  suffix = '',
  fractional = 2,
  animated: animatedProp = true,
  onChange,
  className,
  ...props
}: NumberInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const caretRef = useRef<number>(0);
  const autoId = useId();
  const id = idProp ?? autoId;

  const [inputRaw, setInputRaw] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);
  const [animated, setAnimated] = useState(animatedProp);
  const [showCaret, setShowCaret] = useState(true);

  const displayValue = useMemo(() => {
    if (!inputValue) return '';
    return `${prefix}${inputValue}${suffix}`;
  }, [inputValue, prefix, suffix]);

  useEffect(() => {
    setInputRaw(inputValue);
  }, [inputValue]);

  // Sync from `value` → display, only when not editing:
  useEffect(() => {
    if (isFocused) return;
    if (value === undefined || value === 0) {
      setInputValue('');
    } else {
      const dec = capNumber({ value, min, max }).toDecimalPlaces(fractional);
      setInputValue(formatMoney(dec.toNumber(), { decimals: 0, maxDecimals: fractional }));
    }
  }, [value, isFocused, fractional, min, max]);

  const isValid = useMemo(() => {
    if (!inputRaw) {
      return true;
    }
    try {
      const stripped = strip(inputRaw) as NumericString;
      return (
        isNumberWithinBounds({
          value: stripped,
          max,
        }) && Decimal(stripped).toDecimalPlaces(fractional).eq(Decimal(stripped))
      );
    } catch {
      return false;
    }
  }, [fractional, inputRaw, max]);

  const handleInputChange = useCallback(
    (raw: string) => {
      setAnimated(false);
      setInputRaw(raw);
      if (!raw) {
        setInputValue('');
        onChange?.(0);
        return;
      }
      const cleaned = strip(raw);
      const parts = cleaned.split('.');
      if (parts.length > 2 || (parts[1]?.length ?? 0) > fractional) {
        return;
      }

      // parse using Decimal
      const rawValue = cleaned === '' || cleaned === '.' ? '0' : cleaned;
      const decRaw = new Decimal(rawValue);
      const decClamped = capNumber({ value: decRaw, max }).toDecimalPlaces(fractional);

      // if out of bounds, snap immediately
      if (!decRaw.eq(decClamped)) {
        const formatted = formatNumberString(decClamped.toString(), fractional > 0);
        setInputValue(formatted);
        onChange?.(decClamped.toNumber());
        return;
      }

      const formatted = formatNumberString(cleaned, fractional > 0);
      // within bounds
      setInputValue(formatted);
      onChange?.(decClamped.toNumber());
    },
    [max, onChange, fractional],
  );

  // reposition input caret after formatting changes
  useEffect(() => {
    if (!inputRef.current) return;
    // With prefix/suffix: always place caret just after the number (before the suffix)
    if (prefix || suffix) {
      const end = (prefix?.length ?? 0) + inputValue.length;
      inputRef.current.setSelectionRange(end, end);
      return;
    }
    // If the string actually changed formatting (length differs), remap the caret:
    if (inputRaw !== inputValue) {
      const oldCaret = caretRef.current;
      const newCaret = mapRawPosToFormatted(inputRaw, inputValue, oldCaret);
      inputRef.current.setSelectionRange(newCaret, newCaret);
    }
  }, [inputRaw, inputValue, prefix, suffix]);

  const handleMinus = useCallback(() => {
    setAnimated(true);
    // ← Decimal subtraction + clamp + rounding
    const dec = capNumber({ value: Decimal(value ?? 0).minus(step), min, max }).toDecimalPlaces(
      fractional,
    );

    const num = dec.toNumber();
    onChange?.(num);
    setInputValue(formatMoney(num, { decimals: 0, maxDecimals: fractional }));
  }, [value, step, min, max, fractional, onChange]);

  const handlePlus = useCallback(() => {
    setAnimated(true);
    // ← Decimal subtraction + clamp + rounding
    const dec = capNumber({ value: Decimal(value ?? 0).plus(step), min, max }).toDecimalPlaces(
      fractional,
    );

    const num = dec.toNumber();
    onChange?.(num);
    setInputValue(formatMoney(num, { decimals: 0, maxDecimals: fractional }));
  }, [value, step, min, max, fractional, onChange]);

  const numericValue = Number(value ?? 0);
  const minusDisabled = disabled || numericValue <= min;
  const plusDisabled = disabled || numericValue >= max;

  return (
    <div
      className={cn('aria-invalid:animate-shake relative flex h-12 items-center', className)}
      aria-invalid={!isValid}
    >
      {showButtons ? (
        <Button
          className="absolute left-0 z-10 h-full pl-4"
          variant="icon"
          onClick={handleMinus}
          disabled={minusDisabled}
        >
          <Icon size="24px" name="Minus" />
        </Button>
      ) : null}

      <div
        className="relative grid flex-1 items-center justify-items-center text-center [grid-template-areas:'overlap']
          *:[grid-area:overlap]"
      >
        <Input
          {...props}
          id={id}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          placeholder={placeholder}
          value={displayValue}
          onFocus={(e) => {
            setIsFocused(true);
            setAnimated(false);
            if (prefix || suffix) {
              const end = (prefix?.length ?? 0) + inputValue.length;
              requestAnimationFrame(() => {
                e.target.setSelectionRange(end, end);
              });
            }
          }}
          onClick={(e) => {
            if (prefix || suffix) {
              const end = (prefix?.length ?? 0) + inputValue.length;
              (e.target as HTMLInputElement).setSelectionRange(end, end);
            }
          }}
          onBlur={() => {
            setIsFocused(false);
            setAnimated(true);
          }}
          onChange={(e) => {
            const target = e.target as HTMLInputElement;
            let raw = target.value;
            if (prefix) raw = raw.split(prefix).join('');
            if (suffix) raw = raw.split(suffix).join('');
            caretRef.current = target.selectionStart ?? 0;
            handleInputChange(raw);
          }}
          ref={inputRef}
          disabled={disabled}
          className={cn(
            'h-full px-2 text-center sm:block border-0 !bg-transparent shadow-none',
            'text-[16px] font-medium leading-[22px] text-v2-text-primary placeholder:text-v2-text-secondary',
            { 'px-10': showButtons },
            showCaret ? 'caret-primary' : 'caret-transparent',
            'font-[inherit]',
            !isFocused && inputValue && animatedProp && 'text-transparent',
          )}
          style={{ fontKerning: 'none' }}
        />
        {!isFocused && inputValue && animatedProp ? (
          <div className="pointer-events-none absolute flex h-full w-full items-center justify-center text-[16px] font-medium leading-[22px] text-v2-text-primary">
            <div className="relative flex items-baseline">
              {prefix && <span className="relative select-none">{prefix}</span>}
              <NumberFlow
                value={Number(strip(inputValue)) || 0}
                format={{
                  useGrouping: true,
                  minimumFractionDigits: 0,
                  maximumFractionDigits: fractional,
                }}
                locales="en-US"
                aria-hidden
                animated={animated}
                onAnimationsStart={() => setShowCaret(false)}
                onAnimationsFinish={() => setShowCaret(true)}
                className="pointer-events-none"
                willChange
              />
              {suffix && <span className="relative select-none">{suffix}</span>}
            </div>
          </div>
        ) : null}
      </div>

      {showButtons ? (
        <Button
          className="absolute right-0 z-10 h-full pr-4"
          variant="icon"
          onClick={handlePlus}
          disabled={plusDisabled}
        >
          <Icon size="24px" name="Plus" />
        </Button>
      ) : null}
    </div>
  );
}
